import { useEditorParseData } from 'components/FormulaEditor';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimContractOtherCostUpdateInput, BimWorkOrderOtherPriceData, ConstructionTypeData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    bimUpdateWorkOrderOtherForActGql,
    bimUpdateWorkOrderOtherGql,
    bimWorkOrderOtherDataGql,
} from '../../BimWorkOrderOthersPrice/bimWorkOrderOthersQueries';

interface IUseBimMethodsEditOther {
    isOpenModal: boolean;
    dataRow: BimWorkOrderOtherPriceData | undefined;
    resetIncomingData: () => void;
    statusWorkOrder: WorkOrderStatusEnum;
    bimWorkOrderConstructionTypes: ConstructionTypeData[];
}

const requestId = 'bimUpdateWorkOrderOther';

export const useBimMethodsEditOther = (props: IUseBimMethodsEditOther) => {
    const dispatch = useDispatch();

    const { isOpenModal, dataRow, resetIncomingData, statusWorkOrder, bimWorkOrderConstructionTypes } = props;

    const isInProgress = useMemo(() => statusWorkOrder === WorkOrderStatusEnum.InProgress, [statusWorkOrder]);

    const variables = useMemo(
        () => ({
            workOrderOtherPriceId: dataRow?.id,
        }),
        [dataRow?.id],
    );

    const [bimWorkOrderOtherData, bimWorkOrderOtherStatus] = useRequest({
        requestId: 'bimWorkOrderOtherData',
        query: bimWorkOrderOtherDataGql,
        variables,
        withEffect: isOpenModal && Boolean(dataRow?.id),
    });

    const [resetBimWorkOrderOtherData] = useResetStoreData({
        requestId: 'bimWorkOrderOtherData',
    });

    useEffect(
        () => () => {
            resetBimWorkOrderOtherData(undefined);
        },
        [resetBimWorkOrderOtherData],
    );

    const bimOtherData = bimWorkOrderOtherData?.bimWorkOrderOtherData?.data;

    const [bimUpdateWorkOrderOtherData, bimUpdateWorkOrderOtherStatus, , bimUpdateWorkOrderOtherRequest] = useRequest({
        requestId,
        query: bimUpdateWorkOrderOtherGql,
        withEffect: false,
    });

    const [
        bimUpdateWorkOrderOtherForActData,
        bimUpdateWorkOrderOtherForActStatus,
        ,
        bimUpdateWorkOrderOtherForActRequest,
    ] = useRequest({
        requestId,
        query: bimUpdateWorkOrderOtherForActGql,
        withEffect: false,
    });

    const [resetBimUpdateWorkOrderOther] = useResetStoreData({
        requestId,
    });

    const isUpdateFetching = useMemo(
        () =>
            bimUpdateWorkOrderOtherStatus === RequestStatus.FETCHING ||
            bimUpdateWorkOrderOtherForActStatus === RequestStatus.FETCHING,
        [bimUpdateWorkOrderOtherForActStatus, bimUpdateWorkOrderOtherStatus],
    );

    const { serializeStringForBackend } = useEditorParseData({});

    const onUpdateOtherEdit = useCallback(
        (dataSubmit: any) => {
            if (!bimOtherData) {
                return;
            }

            const transformOtherCostLimits = dataSubmit.otherCostLimits
                .filter((el: any, index: number) => el[`checkbox_${bimWorkOrderConstructionTypes?.[index]?.id}`])
                .map((limit: Record<string, number>) =>
                    Object.entries(limit).reduce((acc: {}, [key, value]) => {
                        if (value) {
                            if (key.includes('checkbox')) {
                                acc = { ...acc, constructionTypeId: Number(key.split('_')[1]) };
                            }

                            if (key.includes('cost')) {
                                acc = { ...acc, cost: replacerCost(value) };
                            }
                        }

                        return acc;
                    }, {}),
                );

            const input: BimContractOtherCostUpdateInput = {
                calculatingType: bimOtherData?.calculatingType,
                calculatingTypeValue: replacerCost(dataSubmit?.calculatingTypeValue),
                contractId: bimOtherData?.contractId,
                id: bimOtherData?.id,
                name: dataSubmit.name,
                otherCostLimits: transformOtherCostLimits,
                section: bimOtherData?.section,
                catalogOtherCostTypeId: dataSubmit?.otherCostType?.id,
                formula: serializeStringForBackend(dataSubmit?.formula),
            };

            const workOrderOtherPriceId = dataRow?.id;

            if (isInProgress) {
                dispatch(
                    bimUpdateWorkOrderOtherForActRequest({
                        workOrderOtherPriceId,
                        input: { ...input, rationale: dataSubmit.rationale },
                    }),
                );
            } else {
                dispatch(bimUpdateWorkOrderOtherRequest({ workOrderOtherPriceId, input }));
            }
        },
        [
            bimOtherData,
            serializeStringForBackend,
            dataRow?.id,
            isInProgress,
            bimWorkOrderConstructionTypes,
            dispatch,
            bimUpdateWorkOrderOtherForActRequest,
            bimUpdateWorkOrderOtherRequest,
        ],
    );

    useEffect(() => {
        if (
            !isUpdateFetching &&
            (bimUpdateWorkOrderOtherData?.bimUpdateWorkOrderOther?.result ||
                bimUpdateWorkOrderOtherForActData?.bimUpdateWorkOrderOtherForAct?.result)
        ) {
            resetBimWorkOrderOtherData(undefined);
            resetBimUpdateWorkOrderOther(undefined);
            resetIncomingData();
        }
    }, [
        bimUpdateWorkOrderOtherData?.bimUpdateWorkOrderOther?.result,
        bimUpdateWorkOrderOtherForActData?.bimUpdateWorkOrderOtherForAct?.result,
        isUpdateFetching,
        resetBimUpdateWorkOrderOther,
        resetBimWorkOrderOtherData,
        resetIncomingData,
    ]);

    return {
        bimOtherData,
        bimWorkOrderOtherStatus,
        resetBimWorkOrderOtherData,
        onUpdateOtherEdit,
        bimUpdateWorkOrderOtherStatus,
    };
};
